<script setup lang="ts">
import type {
  IDisplayedProgrammingRange,
  ProgrammingRangeDeletionTarget,
} from "@/components/piloting/planning/section-programming-ranges-cell.types";
import type {
  ProgrammingRange,
  SectionNodePlanning,
} from "@/domains/v4b/site-programming-range/site-programming-range.types";

import { storeToRefs } from "pinia";
import { computed } from "vue";

import { buildDisplayedRanges } from "@/components/piloting/planning/section-programming-ranges-utils";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useProgrammingPlanningStore } from "@/stores/piloting/programming-planning-store";

import SectionChildrenProgrammingRangesCount from "@/components/piloting/planning/section-children-programming-ranges-count-cell.vue";
import SectionProgrammingRange from "@/components/piloting/planning/section-programming-range.vue";

const props = defineProps<{
  sectionWithProgrammingRanges: SectionNodePlanning;
}>();
const emits = defineEmits<{
  (e: "click:edit", range: ProgrammingRange): void;
  (e: "click:delete", target: ProgrammingRangeDeletionTarget): void;
}>();

const programmingPlanningStore = useProgrammingPlanningStore();
const { startDate, endDate } = storeToRefs(programmingPlanningStore);

const displayedRanges = computed<IDisplayedProgrammingRange[]>(() => {
  return buildDisplayedRanges(props.sectionWithProgrammingRanges.programmingRanges, startDate, endDate);
});

function handleEditProgrammingRange(range: IDisplayedProgrammingRange) {
  emits("click:edit", range);
}

function handleDeleteProgrammingRange(range: IDisplayedProgrammingRange) {
  emits("click:delete", { uuid: range.uuid, end: range.end, programId: range.program.id });
}
</script>

<template>
  <div
    class="flex w-full h-full flex-row"
    :data-test-id="DATA_TEST_IDS.components.sectionProgrammingRangesCell.component"
    :data-test-section-id="props.sectionWithProgrammingRanges.sectionId"
  >
    <template v-if="props.sectionWithProgrammingRanges.children.length">
      <SectionChildrenProgrammingRangesCount :sectionChildren="props.sectionWithProgrammingRanges" />
    </template>
    <template v-else>
      <SectionProgrammingRange
        v-for="range in displayedRanges"
        :key="range.uuid"
        :program-color="range.program.frontDisplayColor"
        :programming-range-id="range.uuid"
        :displayed-end-date="endDate"
        :displayed-start-date="startDate"
        :programmingRange="range"
        @click:delete="handleDeleteProgrammingRange(range)"
        @click:edit="handleEditProgrammingRange(range)"
      />
    </template>
  </div>
</template>
